import {
  LoyaltyProgramEnum,
  type FlightCredit,
  type FreeNightCredit,
  type LoyaltyAccount,
  type UpgradeCertificate,
} from "@/generated/email_parser.openapi";

/**
 * Hotel loyalty programs that can issue suite upgrade awards / certificates.
 * Values are drawn from `LoyaltyProgramEnum` so stored `programName` strings
 * line up with the rest of the loyalty system (matching, logos, colors).
 */
export const HOTEL_LOYALTY_PROGRAMS: readonly LoyaltyProgramEnum[] = [
  LoyaltyProgramEnum.MarriottBonvoy,
  LoyaltyProgramEnum.HiltonHonors,
  LoyaltyProgramEnum.WorldOfHyatt,
  LoyaltyProgramEnum.IhgOneRewards,
  LoyaltyProgramEnum.WyndhamRewards,
  LoyaltyProgramEnum.AccorLiveLimitless,
  LoyaltyProgramEnum.ChoicePrivilegesRewards,
  LoyaltyProgramEnum.BestWesternRewards,
  LoyaltyProgramEnum.LeadersClub,
] as const;

/**
 * Maps IATA airline codes to loyalty account brandName values.
 * Values must match the proper-case brand_name stored by the backend
 * (sourced from common/airline.py Airline.name via loyalty resolvers).
 */
export const IATA_TO_BRAND_NAME: Record<string, string> = {
  // US domestic
  UA: "United Airlines",
  DL: "Delta Air Lines",
  AA: "American Airlines",
  WN: "Southwest Airlines",
  B6: "JetBlue Airways",
  AS: "Alaska Airlines",
  NK: "Spirit Airlines",
  F9: "Frontier Airlines",
  HA: "Hawaiian Airlines",
  SY: "Sun Country Airlines",
  // International with loyalty programs
  AC: "Air Canada",
  AF: "Air France",
  BA: "British Airways",
  CX: "Cathay Pacific",
  EI: "Aer Lingus",
  EK: "Emirates",
  AM: "Aeromexico",
  AV: "Avianca",
  IB: "Iberia",
  KE: "Korean Air",
  NH: "ANA",
  OS: "Austrian",
  QF: "Qantas",
  QR: "Qatar Airways",
  SQ: "Singapore Airlines",
  VS: "Virgin Atlantic",
};

/**
 * Reverse lookup: brandName → IATA code. Used to pre-fill the airline
 * dropdown when adding a flight credit from a specific loyalty account.
 */
export const BRAND_NAME_TO_IATA: Record<string, string> = Object.fromEntries(
  Object.entries(IATA_TO_BRAND_NAME).map(([iata, name]) => [name, iata])
);

/**
 * Returns flight credits that match a given loyalty account by airline IATA code.
 */
export function getCreditsForAccount(
  credits: FlightCredit[],
  account: LoyaltyAccount
): FlightCredit[] {
  if (account.brandType !== "airline" || !account.brandName) return [];
  return credits.filter((credit) => {
    if (!credit.airline) return false;
    const expectedBrandName = IATA_TO_BRAND_NAME[credit.airline];
    return expectedBrandName === account.brandName;
  });
}

/**
 * Returns upgrade certificates that match a given loyalty account by loyaltyAccountId.
 */
export function getUpgradesForAccount(
  upgrades: UpgradeCertificate[],
  account: LoyaltyAccount
): UpgradeCertificate[] {
  if (!account.id) return [];
  return upgrades.filter((cert) => cert.loyaltyAccountId === account.id);
}

/**
 * Returns flight credits not matched to any loyalty account.
 */
export function getUnmatchedCredits(
  credits: FlightCredit[],
  accounts: LoyaltyAccount[]
): FlightCredit[] {
  const airlineAccounts = accounts.filter(
    (a) => a.brandType === "airline" && a.brandName
  );
  return credits.filter((credit) => {
    if (!credit.airline) return true;
    const expectedBrandName = IATA_TO_BRAND_NAME[credit.airline];
    if (!expectedBrandName) return true;
    return !airlineAccounts.some((a) => a.brandName === expectedBrandName);
  });
}

/**
 * Returns upgrade certificates not matched to any loyalty account.
 */
export function getUnmatchedUpgrades(
  upgrades: UpgradeCertificate[],
  accounts: LoyaltyAccount[]
): UpgradeCertificate[] {
  const accountIds = new Set(accounts.map((a) => a.id).filter(Boolean));
  return upgrades.filter(
    (cert) => !cert.loyaltyAccountId || !accountIds.has(cert.loyaltyAccountId)
  );
}

/**
 * Returns free-night credits that match a given loyalty account by loyaltyAccountId.
 */
export function getFreeNightCreditsForAccount(
  credits: FreeNightCredit[],
  account: LoyaltyAccount
): FreeNightCredit[] {
  if (!account.id) return [];
  return credits.filter((credit) => credit.loyaltyAccountId === account.id);
}

/**
 * Returns free-night credits not matched to any loyalty account.
 */
export function getUnmatchedFreeNightCredits(
  credits: FreeNightCredit[],
  accounts: LoyaltyAccount[]
): FreeNightCredit[] {
  const accountIds = new Set(accounts.map((a) => a.id).filter(Boolean));
  return credits.filter(
    (credit) =>
      !credit.loyaltyAccountId || !accountIds.has(credit.loyaltyAccountId)
  );
}

/**
 * Each hotel chain has exactly one free-night credit type; the type is derived
 * from the program rather than picked by the user. Programs not in this map
 * don't currently support free-night credits (Accor, Choice, Best Western,
 * Leaders Club).
 */
export const PROGRAM_TO_FREE_NIGHT_CREDIT_TYPE: Partial<
  Record<LoyaltyProgramEnum, string>
> = {
  [LoyaltyProgramEnum.MarriottBonvoy]: "marriott_free_night",
  [LoyaltyProgramEnum.HiltonHonors]: "hilton_free_night",
  [LoyaltyProgramEnum.WorldOfHyatt]: "hyatt_free_night",
  [LoyaltyProgramEnum.IhgOneRewards]: "ihg_free_night",
  [LoyaltyProgramEnum.WyndhamRewards]: "wyndham_free_night",
};

/**
 * Programs eligible for free-night credits — used to gate the dropdown in the
 * form sheet and the "Add free night credit" menu item.
 */
export const FREE_NIGHT_CREDIT_PROGRAMS: readonly LoyaltyProgramEnum[] = [
  LoyaltyProgramEnum.MarriottBonvoy,
  LoyaltyProgramEnum.HiltonHonors,
  LoyaltyProgramEnum.WorldOfHyatt,
  LoyaltyProgramEnum.IhgOneRewards,
  LoyaltyProgramEnum.WyndhamRewards,
] as const;

/**
 * Programs where the cap is a points cap (vs. category cap for Hyatt, no cap
 * for Hilton). Drives which input renders in the form.
 */
export const PROGRAMS_WITH_POINTS_CAP: ReadonlySet<LoyaltyProgramEnum> =
  new Set([
    LoyaltyProgramEnum.MarriottBonvoy,
    LoyaltyProgramEnum.IhgOneRewards,
    LoyaltyProgramEnum.WyndhamRewards,
  ]);

/**
 * Hyatt is the only program with category-cap (Cat 1-4, Cat 1-7) semantics.
 */
export const PROGRAMS_WITH_CATEGORY_CAP: ReadonlySet<LoyaltyProgramEnum> =
  new Set([LoyaltyProgramEnum.WorldOfHyatt]);

/**
 * Programs that allow topping up cash/points above the cap.
 */
export const PROGRAMS_WITH_TOP_UP: ReadonlySet<LoyaltyProgramEnum> = new Set([
  LoyaltyProgramEnum.MarriottBonvoy,
  LoyaltyProgramEnum.IhgOneRewards,
]);

/**
 * Short labels for certificate types.
 */
export const CERT_TYPE_SHORT: Record<string, string> = {
  nua: "NUAs",
  sua: "SUAs",
  cur: "CURs",
  ihg_suite_upgrade: "Certs",
};

/**
 * Short labels for free-night credit types.
 */
export const FREE_NIGHT_CREDIT_TYPE_SHORT: Record<string, string> = {
  marriott_free_night: "Free Nights",
  hilton_free_night: "Free Nights",
  hyatt_free_night: "Free Nights",
  ihg_free_night: "Free Nights",
  wyndham_free_night: "Free Nights",
};

/**
 * Full labels for free-night credit types.
 */
export const FREE_NIGHT_CREDIT_TYPE_LABELS: Record<string, string> = {
  marriott_free_night: "Marriott Free Night Award",
  hilton_free_night: "Hilton Free Night Reward",
  hyatt_free_night: "Hyatt Free Night Award",
  ihg_free_night: "IHG Anniversary Free Night",
  wyndham_free_night: "Wyndham Free Night Award",
};

/**
 * Full labels for credit types.
 */
export const CREDIT_TYPE_LABELS: Record<string, string> = {
  flight_credit: "Flight Credit",
  ecredit: "eCredit",
  trip_credit: "Trip Credit",
  travel_fund: "Travel Fund",
  wallet_credit: "Wallet Credit",
  travel_bank_credit: "Travel Bank Credit",
  electronic_travel_certificate: "Electronic Travel Certificate",
  voucher: "Voucher",
  refund: "Refund Credit",
};

/**
 * Full labels for certificate types.
 */
export const CERT_TYPE_LABELS: Record<string, string> = {
  nua: "Nightly Upgrade Awards",
  sua: "Suite Upgrade Awards",
  cur: "Confirmable Upgrade Rewards",
  ihg_suite_upgrade: "Suite Upgrade Certificates",
};

/**
 * Format a date for credit/upgrade display.
 */
export function formatCreditDate(date?: Date | string | null): string {
  if (!date) return "No expiration";
  const d = date instanceof Date ? date : new Date(date);
  if (isNaN(d.getTime())) return "—";
  // expiration_date is `format: date`; parsed as UTC midnight by the generated
  // client. Render in UTC so we don't shift back a day in negative-offset TZs.
  return new Intl.DateTimeFormat("en-US", {
    month: "short",
    day: "numeric",
    year: "numeric",
    timeZone: "UTC",
  }).format(d);
}

/**
 * Calculate days until a date expires.
 */
export function daysUntilExpiration(
  date?: Date | string | null
): number | null {
  if (!date) return null;
  const d = date instanceof Date ? date : new Date(date);
  if (isNaN(d.getTime())) return null;
  const now = new Date();
  const diff = d.getTime() - now.getTime();
  return Math.ceil(diff / (1000 * 60 * 60 * 24));
}

/**
 * Filter out expired flight credits.
 */
export function filterActiveCredits(credits: FlightCredit[]): FlightCredit[] {
  return credits.filter((c) => {
    const days = daysUntilExpiration(c.expirationDate);
    return days === null || days >= 0;
  });
}

/**
 * Filter out expired upgrade certificates.
 */
export function filterActiveCertificates(
  certs: UpgradeCertificate[]
): UpgradeCertificate[] {
  return certs.filter((c) => {
    const days = daysUntilExpiration(c.expirationDate);
    return days === null || days >= 0;
  });
}

/**
 * Filter out expired free-night credits.
 */
export function filterActiveFreeNightCredits(
  credits: FreeNightCredit[]
): FreeNightCredit[] {
  return credits.filter((c) => {
    const days = daysUntilExpiration(c.expirationDate);
    return days === null || days >= 0;
  });
}
